import { Form, Row, Col, DatePicker, Button, Table, message } from "antd";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { getRangeOptions } from './reports.module';
import { getAPIDateFormat } from "../../utils/date.utils";
import { loadingAction } from "../../actions/loading-actions";
import { CheckSquareFilled, PrinterOutlined, ExportOutlined } from '@ant-design/icons';
import { getDriverAttendanceReportAction, getDriverServicesReportAction } from '../../actions/reports.action';
import moment from 'moment';
import './driver-attendance.report.scss';

const { RangePicker } = DatePicker;
let allowedMaxDaysRange = 31;

export const DriverAttendanceReport = (props) => {

    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [attendanceData, setAttendanceData] = useState([]);
    const [dateColumns, setDateColumns] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [servicesData, setServicesData] = useState([]);

    const onSuccess = (response) => {
        const { dateRange, drivers } = response;
        
        // Generate dynamic date columns from API response
        if (dateRange && dateRange.length > 0) {
            const dynamicColumns = dateRange.map(dateStr => {
                const displayDate = moment(dateStr).format('MMM D');
                return {
                    title: displayDate,
                    dataIndex: dateStr,
                    key: dateStr,
                    width: 55,
                    align: 'center',
                    render: (value) => {
                        if (value) {
                            return (
                                <div className="attendance-check">
                                    <CheckSquareFilled  style={{ color: '#22c55e', fontSize: '20px' }} />
                                </div>
                            );
                        }
                        return null;
                    }
                };
            });
            setDateColumns(dynamicColumns);
        }
        
        // Transform drivers data for table
        const tableData = drivers.map(driver => ({
            key: driver.id,
            driverId: driver.id,
            driverName: driver.name,
            phoneNumber: driver.phone_number,
            summary: driver.summary,
            presentDays: driver.presentDays,
            ...driver.attendance // Spread attendance dates as properties
        }));
        
        setAttendanceData(tableData);
        setLoading(false);
    }

    const onFailure = (errorMessage) => {
        message.error(errorMessage || 'Failed to fetch driver attendance report');
        setLoading(false);
    }

    const onServicesSuccess = (response) => {
        const { services } = response || {};
        setServicesData(Array.isArray(services) ? services : []);
    }

    const onServicesFailure = (errorMessage) => {
        message.error(errorMessage || 'Failed to fetch driver services report');
        setServicesData([]);
    }

    const onFinish = (values) => {
        let { dateRange } = values;
        setLoading(true);
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        setSelectedDateRange({ fromDate, toDate });

        getDriverAttendanceReportAction(fromDate, toDate, onSuccess, onFailure);
        getDriverServicesReportAction(fromDate, toDate, onServicesSuccess, onServicesFailure);
    }

    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    }

    const baseColumns = [
        {
            title: "Driver Name",
            dataIndex: "driverName",
            key: "driverName",
            fixed: 'left',
            align: 'center',
            width: 130,
            render: (text, record) => (
                <div className="driver-info">
                    <div className="driver-name">{text}</div>
                    <div className="driver-phone">{record.phoneNumber}</div>
                </div>
            )
        },
        {
            title: "Summary",
            dataIndex: "summary",
            key: "summary",
            fixed: 'left',
            width: 75,
            align: 'center',
            render: (value) => value // Already formatted as "X days" from API
        }
    ];

    const columns = [...baseColumns, ...dateColumns];

    // Build antd column filter options ({ text, value }) from the unique,
    // non-empty values the accessor pulls out of the loaded services data.
    const buildColumnFilters = (accessor) => {
        const unique = Array.from(
            new Set(
                servicesData
                    .map(accessor)
                    .filter((value) => value !== null && value !== undefined && value !== '')
            )
        );
        return unique
            .sort((a, b) => String(a).localeCompare(String(b)))
            .map((value) => ({ text: value, value }));
    };

    const renderStaff = (name, phone) => {
        if (!name && !phone) {
            return null;
        }
        return (
            <div className="service-staff">
                {name && <div className="staff-name">{name}</div>}
                {phone && <div className="staff-phone">Ph: {phone}</div>}
            </div>
        );
    };

    const servicesColumns = [
        {
            title: "Sr No",
            key: "srNo",
            align: "center",
            render: (text, record, index) => index + 1
        },
        {
            title: "Service Number",
            dataIndex: "service_name",
            key: "service_name",
            align: "left"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (value) => (
                <span className={value === 'Active' ? 'status-active' : 'status-stop'}>
                    {value}
                </span>
            )
        },
        {
            title: "Route Name",
            dataIndex: "route_name",
            key: "route_name",
            align: "left"
        },
        {
            title: "Boarding Point",
            dataIndex: "boarding_point",
            key: "boarding_point",
            align: "left"
        },
        {
            title: "Boarding Time",
            dataIndex: "boarding_time",
            key: "boarding_time",
            align: "center",
            render: (value) => (value ? value.substring(0, 5) : null)
        },
        {
            title: "Travel Date",
            dataIndex: "travel_date",
            key: "travel_date",
            align: "center",
            render: (value) => (value ? moment(value).format('DD/MM/YYYY') : null)
        },
        {
            title: "Coach No",
            dataIndex: "coach_no",
            key: "coach_no",
            align: "left",
            filters: buildColumnFilters((service) => service.coach_no),
            onFilter: (value, record) => record.coach_no === value
        },
        {
            title: "Bus Type",
            dataIndex: "bus_type",
            key: "bus_type",
            align: "left"
        },
        {
            title: "Driver1",
            key: "driver1",
            align: "left",
            filters: buildColumnFilters((service) => service.driver_name),
            onFilter: (value, record) => record.driver_name === value,
            render: (text, record) => renderStaff(record.driver_name, record.driver_phone)
        },
        {
            title: "Driver2",
            key: "driver2",
            align: "left",
            filters: buildColumnFilters((service) => service.second_driver_name),
            onFilter: (value, record) => record.second_driver_name === value,
            render: (text, record) => renderStaff(record.second_driver_name, record.second_driver_phone)
        },
        {
            title: "Attender",
            key: "attender",
            align: "left",
            render: (text, record) => renderStaff(record.attender_name, record.attender_phone)
        }
    ];

    const handlePrint = () => {
        window.print();
    }

    const handleExport = () => {
        if (attendanceData.length === 0 && servicesData.length === 0) {
            message.warning('No data to export');
            return;
        }

        // Create CSV content
        let csvContent = "Driver Name,Phone Number,Summary";
        
        // Add date column headers
        dateColumns.forEach(col => {
            csvContent += `,${col.title}`;
        });
        csvContent += "\n";

        // Add data rows
        attendanceData.forEach(row => {
            csvContent += `"${row.driverName}","${row.phoneNumber}","${row.summary}"`;
            dateColumns.forEach(col => {
                csvContent += `,${row[col.dataIndex] ? 'Present' : 'Absent'}`;
            });
            csvContent += "\n";
        });

        // Driver Report (per-service trip details) section
        if (servicesData.length > 0) {
            const csvCell = (value) => `"${(value === null || value === undefined) ? '' : String(value).replace(/"/g, '""')}"`;
            const staffCell = (name, phone) => [name, phone].filter(Boolean).join(' Ph: ');

            csvContent += "\nDriver Report\n";
            csvContent += "Sr No,Service Number,Status,Route Name,Boarding Point,Boarding Time,Travel Date,Coach No,Bus Type,Driver1,Driver2,Attender\n";
            servicesData.forEach((service, index) => {
                csvContent += [
                    index + 1,
                    csvCell(service.service_name),
                    csvCell(service.status),
                    csvCell(service.route_name),
                    csvCell(service.boarding_point),
                    csvCell(service.boarding_time ? service.boarding_time.substring(0, 5) : ''),
                    csvCell(service.travel_date ? moment(service.travel_date).format('DD/MM/YYYY') : ''),
                    csvCell(service.coach_no),
                    csvCell(service.bus_type),
                    csvCell(staffCell(service.driver_name, service.driver_phone)),
                    csvCell(staffCell(service.second_driver_name, service.second_driver_phone)),
                    csvCell(staffCell(service.attender_name, service.attender_phone))
                ].join(',') + "\n";
            });
        }

        // Download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `driver_attendance_${selectedDateRange?.fromDate}_to_${selectedDateRange?.toDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    return (
        <div className="driver-attendance-report">
            <Form
                onFinish={onFinish}
                form={form}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col>
                        <Form.Item 
                            name="dateRange" 
                            label="Date range"
                            rules={[
                                { required: true, message: 'Please select date range!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                    },
                                })
                            ]}
                        >
                            <RangePicker
                                ranges={getRangeOptions(allowedMaxDaysRange)}
                                format="YYYY-MM-DD"
                            />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" className="search-btn">
                                Search
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            
            <Row gutter={[8, 16]} className="action-buttons">
                <Col>
                    <Button 
                        icon={<PrinterOutlined />} 
                        onClick={handlePrint}
                        className="print-btn"
                    >
                        Print
                    </Button>
                </Col>
                <Col>
                    <Button 
                        icon={<ExportOutlined />} 
                        onClick={handleExport}
                        className="export-btn"
                    >
                        Export
                    </Button>
                </Col>
            </Row>

            <Row className="table-container">
                <Col span={24}>
                    <Table
                        columns={columns}
                        dataSource={attendanceData}
                        pagination={false}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        className="attendance-table"
                        rowKey={(record) => record.driverId || record.phoneNumber}
                        rowClassName={(record, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}
                    />
                </Col>
            </Row>

            {servicesData.length > 0 &&
                <>
                    <Row className="services-title">
                        <Col span={24}>
                            <h3>Driver Report</h3>
                            <div className="title-underline"></div>
                        </Col>
                    </Row>
                    <Row className="table-container">
                        <Col span={24}>
                            <Table
                                columns={servicesColumns}
                                dataSource={servicesData}
                                pagination={false}
                                size="small"
                                scroll={{ x: 'max-content' }}
                                className="attendance-table services-table"
                                rowKey={(record) => record.id}
                                rowClassName={(record, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}
                            />
                        </Col>
                    </Row>
                </>
            }
        </div>
    )
}

